"use client";

import { useEffect, useMemo, useState } from "react";

import { fetchAirlines } from "@/features/flight-estimator/_logic/airline-search";
import { DEBOUNCE_DELAY } from "@/features/flight-estimator/_logic/constants";
import type { AirlineData } from "@/features/flight-estimator/_logic/types";
import { createClient } from "@/utils/supabase/client";

import { AIRLINE_CODE_PATTERN, AIRLINE_SEARCH_LIMIT } from "../_logic/constants";

/** An airline search as the picker drives it. */
export interface AirlineSearch {
  query: string;
  setQuery: (query: string) => void;
  /** Airlines matching the query, by IATA code or name; only ones with a usable code. */
  results: AirlineData[];
  loading: boolean;
}

/**
 * Searches the airline directory as the planner types, debounced, keeping only the latest answer.
 * @returns The query, its setter, the results and whether a lookup is running.
 * @throws Never; a failed lookup shows no results.
 */
export function useAirlineSearch(): AirlineSearch {
  // Hooks
  const supabase = useMemo(() => createClient(), []);

  // States
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<AirlineData[]>([]);
  const [loading, setLoading] = useState(false);

  // Effects
  useEffect(() => {
    const term = query.trim();
    if (!term) {
      setResults([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(async () => {
      const found = await fetchAirlines(supabase, term, AIRLINE_SEARCH_LIMIT).catch(() => []);
      if (cancelled) return;
      setResults(found.filter((airline) => AIRLINE_CODE_PATTERN.test(airline.iata_code?.toUpperCase() ?? "")));
      setLoading(false);
    }, DEBOUNCE_DELAY);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, supabase]);

  return { query, setQuery, results, loading };
}
